import { useMemo, useState } from "react";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/mockups/ui";
import { TrendingUp, TrendingDown, Minus, Users, UserCog } from "lucide-react";

const euro = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const euro2 = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }));

const METRICS = [
  { key: "coste", label: "Coste empresa" },
  { key: "gross", label: "Bruto" },
  { key: "net", label: "Neto" },
  { key: "irpf", label: "IRPF" },
];

function MESES(period) {
  if (!period || !period.includes("-")) return period || "—";
  const [y, m] = period.split("-");
  const names = ["", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${names[parseInt(m, 10)] || m} ${y.slice(2)}`;
}

function Yoy({ rows, years, metric }) {
  if (years.length < 2) return null;
  const [prev, last] = [years[years.length - 2], years[years.length - 1]];
  const a = rows.reduce((s, r) => s + (r.years[String(prev)]?.[metric] || 0), 0);
  const b = rows.reduce((s, r) => s + (r.years[String(last)]?.[metric] || 0), 0);
  if (!a) return null;
  const pct = ((b - a) / a) * 100;
  const Icon = pct > 0.5 ? TrendingUp : pct < -0.5 ? TrendingDown : Minus;
  const color = pct > 0.5 ? "text-red-600" : pct < -0.5 ? "text-emerald-600" : "text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`} data-testid="salary-yoy">
      <Icon className="h-3.5 w-3.5" />{pct > 0 ? "+" : ""}{pct.toFixed(1)}% {prev}→{last}
    </span>
  );
}

function yoyCell(row, years, metric) {
  if (years.length < 2) return null;
  const [prev, last] = [years[years.length - 2], years[years.length - 1]];
  const a = row.years[String(prev)]?.[metric] || 0;
  const b = row.years[String(last)]?.[metric] || 0;
  if (!a) return <span className="text-muted-foreground">—</span>;
  const pct = ((b - a) / a) * 100;
  const color = pct > 0.5 ? "text-red-600" : pct < -0.5 ? "text-emerald-600" : "text-muted-foreground";
  return <span className={`text-xs font-semibold ${color}`}>{pct > 0 ? "+" : ""}{pct.toFixed(1)}%</span>;
}

function SalaryTable({ rows, years, metric, emptyText }) {
  if (!rows.length) return <div className="py-10 text-center text-sm text-muted-foreground">{emptyText}</div>;
  const totals = {};
  years.forEach((y) => { totals[y] = rows.reduce((s, r) => s + (r.years[String(y)]?.[metric] || 0), 0); });
  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[720px]">
        <TableHeader>
          <TableRow>
            <TableHead className="sticky left-0 bg-background">Empleado</TableHead>
            {years.map((y) => <TableHead key={y} className="text-right">{y}</TableHead>)}
            {years.length >= 2 && <TableHead className="text-right">Var. YoY</TableHead>}
            <TableHead className="text-right">Última nómina</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`salary-row-${r.id}`}>
              <TableCell className="sticky left-0 bg-background font-medium">{r.name}</TableCell>
              {years.map((y) => {
                const cell = r.years[String(y)];
                return (
                  <TableCell key={y} className="text-right tabular-nums">
                    {cell ? (
                      <span title={`${cell.count} nómina(s)`}>{euro(cell[metric])}</span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                );
              })}
              {years.length >= 2 && <TableCell className="text-right">{yoyCell(r, years, metric)}</TableCell>}
              <TableCell className="text-right tabular-nums">
                {r.last ? (
                  <span className="whitespace-nowrap">{euro2(r.last[metric])} <span className="text-xs text-muted-foreground">· {MESES(r.last.period)}</span></span>
                ) : <span className="text-muted-foreground">—</span>}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="border-t-2 border-border font-bold bg-muted/40">
            <TableCell className="sticky left-0 bg-muted/40">TOTAL ({rows.length})</TableCell>
            {years.map((y) => <TableCell key={y} className="text-right tabular-nums">{euro(totals[y])}</TableCell>)}
            {years.length >= 2 && <TableCell className="text-right"><Yoy rows={rows} years={years} metric={metric} /></TableCell>}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export default function ResumenSalarios() {
  const { data, loading } = useFetch("/hr/salary-report");
  const [metric, setMetric] = useState("coste");
  const [tab, setTab] = useState("team");

  const years = data?.years || [];
  const team = data?.team || [];
  const temporary = data?.temporary || [];
  const rows = tab === "team" ? team : temporary;
  const metricLabel = useMemo(() => METRICS.find((m) => m.key === metric)?.label, [metric]);

  return (
    <div className="space-y-5 p-1" data-testid="resumen-salarios-page">
      <PageHeader title="Resumen de salarios" subtitle="Agregado anual por empleado y última nómina, para comparar año a año. Los perfiles temporales (sin cuenta de plataforma) van en su propia pestaña." />

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Tabs */}
        <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/40 p-1">
          <button onClick={() => setTab("team")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === "team" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid="salary-tab-team">
            <Users className="h-4 w-4" /> Empleados <span className="text-xs text-muted-foreground">({team.length})</span>
          </button>
          <button onClick={() => setTab("temp")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === "temp" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid="salary-tab-temp">
            <UserCog className="h-4 w-4" /> Perfiles temporales <span className="text-xs text-muted-foreground">({temporary.length})</span>
          </button>
        </div>
        {/* Metric selector */}
        <div className="flex items-center gap-1 rounded-lg border border-border p-1">
          {METRICS.map((m) => (
            <button key={m.key} onClick={() => setMetric(m.key)} className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${metric === m.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`salary-metric-${m.key}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-border p-4">
        <p className="mb-3 text-xs text-muted-foreground">Cifras anuales de <span className="font-semibold text-foreground">{metricLabel}</span>. "Última nómina" muestra el importe y mes del último recibo registrado.</p>
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Cargando…</div>
        ) : (
          <SalaryTable
            rows={rows}
            years={years}
            metric={metric}
            emptyText={tab === "team" ? "Aún no hay nóminas de empleados con perfil." : "No hay perfiles temporales con nóminas."}
          />
        )}
      </Card>
    </div>
  );
}
